/*
 * Signal — collection view toggle.
 *
 * The grid/list choice is one attribute on <html>, not state inside the section.
 * That is deliberate: Horizon's <results-list> replaces this section's markup
 * every time a facet or sort changes, so anything held inside it is lost on the
 * first filter click. layout/theme.liquid resolves the attribute before first
 * paint; this file only handles changing it.
 *
 * The visible pressed state is painted by signal-collection.css from that same
 * attribute, which is why nothing here touches class names — CSS is already
 * correct in the first frame and after any re-render. What CSS cannot do is set
 * aria-pressed, so that is the one thing kept in step here.
 *
 * Plain custom-element-free script rather than a Horizon component base, matching
 * signal-header.js: nothing here needs the component lifecycle.
 */

(function signalCollectionView() {
  const STORAGE_KEY = 'signal-view';
  const ATTR = 'signalView';
  const SELECTOR = '[data-signal-view-set]';

  /** @returns {string} the active view, defaulting to grid */
  function current() {
    return document.documentElement.dataset[ATTR] === 'list' ? 'list' : 'grid';
  }

  /**
   * Brings every toggle on the page in line with the active view. Called after a
   * change and whenever new toggles appear, because a re-rendered section brings
   * buttons whose server-rendered aria-pressed assumes grid.
   */
  function sync() {
    const view = current();

    for (const button of document.querySelectorAll(SELECTOR)) {
      button.setAttribute('aria-pressed', String(button.dataset.signalViewSet === view));
    }
  }

  /** @param {string} view */
  function apply(view) {
    if (view === 'list') {
      document.documentElement.dataset[ATTR] = 'list';
    } else {
      delete document.documentElement.dataset[ATTR];
    }

    try {
      localStorage.setItem(STORAGE_KEY, view);
    } catch (error) {
      // Storage is unavailable; the choice still applies for this page view.
    }

    sync();
  }

  // Delegated so it keeps working on buttons that did not exist at load.
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target.closest(SELECTOR) : null;

    if (!target) return;

    apply(target.dataset.signalViewSet === 'list' ? 'list' : 'grid');
  });

  /*
   * A facet change swaps in fresh markup rather than mutating the existing
   * buttons, so there is no event to listen for that every path fires. Watching
   * for the nodes themselves covers filtering, sorting and infinite scroll
   * without knowing which one happened.
   */
  const observer = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node instanceof Element && (node.matches(SELECTOR) || node.querySelector(SELECTOR))) {
          sync();
          return;
        }
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });

  sync();
})();
